import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { OrgContextService } from '../organization/org-context.service';
import { PennyPortalConfig, PennyPortalService } from './penny-portal.service';

export const PENNY_DRAWER_WIDTH_PX = 400;

export type PennyMode = 'drawer' | 'expanded';

/** Singleton state for the homeowner Ask Penny drawer: open/expanded, lazy portal config, session conversation id. */
@Injectable({
    providedIn: 'root',
})
export class PennyDrawerService {
    /** Homeowner-context + feature-flag gate; recomputed on every org-context change. */
    private _eligible: boolean;

    private readonly _isOpen$ = new BehaviorSubject<boolean>(false);
    readonly isOpen$: Observable<boolean> = this._isOpen$.asObservable();

    private readonly _mode$ = new BehaviorSubject<PennyMode>('drawer');
    readonly mode$: Observable<PennyMode> = this._mode$.asObservable();

    private readonly _config$ = new BehaviorSubject<PennyPortalConfig | null>(null);
    readonly config$: Observable<PennyPortalConfig | null> = this._config$.asObservable();

    /** Survives drawer close so reopening resumes the same thread. */
    conversationId: string | null = null;

    private _configRequested = false;

    constructor(
            private _portal: PennyPortalService,
            private _orgContext: OrgContextService,
    ) {
        this._eligible = this._computeEligibility();
        // Root singleton survives in-app org switches — reset so stale eligibility/config/conversation never leak (mirrors GableDrawerService, SD-7898).
        this._orgContext.orgContextChanged$.subscribe(() => this._resetForOrgContextChange());
    }

    get eligible(): boolean {
        return this._eligible;
    }

    /** JWT decoded once per compute — read() in a CD-bound getter logs users out at token expiry (see gable-ask-button). */
    private _computeEligibility(): boolean {
        const jwt = LegFiJwtService.read();
        return !!jwt
                && jwt.superUser !== true
                && jwt.admin !== true
                && !!jwt.unitId
                && LegFiJwtService.featureEnabled('homeowner-penny');
    }

    /** Fresh slate for the new org: re-gate from the new token, drop the old org's config + thread, close the drawer. */
    private _resetForOrgContextChange(): void {
        this._eligible = this._computeEligibility();
        this._config$.next(null);
        this._configRequested = false;
        this.conversationId = null;
        this.close();
    }

    get config(): PennyPortalConfig | null {
        return this._config$.value;
    }

    /** True only once the config fetch confirmed the widget is on for this org. */
    get widgetEnabled(): boolean {
        return this.eligible && this._config$.value?.enabled === true;
    }

    /** Lazy one-shot config fetch — call from any entry point; no-ops when ineligible or already requested. */
    ensureConfigLoaded(): void {
        if (!this.eligible || this._configRequested) {
            return;
        }
        this._configRequested = true;
        this._portal.getConfig().subscribe({
            next: (config) => this._config$.next(config),
            error: () => this._config$.next({ enabled: false }),
        });
    }

    open(): void {
        if (!this._isOpen$.value) {
            this._isOpen$.next(true);
        }
    }

    close(): void {
        if (this._isOpen$.value) {
            this._isOpen$.next(false);
        }
    }

    toggle(): void {
        this._isOpen$.next(!this._isOpen$.value);
    }

    get mode(): PennyMode {
        return this._mode$.value;
    }

    setMode(mode: PennyMode): void {
        if (mode !== this._mode$.value) {
            this._mode$.next(mode);
        }
    }

    toggleMode(): void {
        this.setMode(this._mode$.value === 'drawer' ? 'expanded' : 'drawer');
    }
}
